import { useEffect, useState } from 'react'
import { Lock, Loader2, X } from 'lucide-react'
import { useWaitingRoomStore } from '@os/spaces/web/stores/waitingRoomStore'
import { getSocket } from '../../services/socket'
import { useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'

interface Props {
  channelId: string
}

/**
 * Full-area overlay shown while the user is in the waiting room for a
 * locked channel. The actual LiveKit join is gated on the server — this
 * is purely a UX shell. When the host approves, `voice:waiting:approved`
 * fires and the parent retries the connect flow.
 */
export default function WaitingRoomView({ channelId }: Props) {
  const self = useWaitingRoomStore((s) => s.self)
  const clearSelf = useWaitingRoomStore((s) => s.clearSelf)
  const leaveChannel = useVoiceStore((s) => s.leaveChannel)
  // If the LiveKit room is up for THIS channel, the user has already been
  // admitted and is publishing — the overlay must hide even if the
  // voice:waiting:approved socket event was lost. Belt-and-braces against
  // the same OSPROD-44 class of "broadcast dropped, UI sticks until reload".
  const livekitRoom = useLivekitStore((s) => s.room)
  const isLivekitJoinedHere = !!livekitRoom && (livekitRoom as any)?.name === channelId
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const t = setInterval(() => setElapsed((v) => v + 1), 1000)
    return () => clearInterval(t)
  }, [])

  // When LiveKit comes up for the channel we were waiting on, drop our
  // self-state so the overlay won't reappear after a re-render.
  useEffect(() => {
    if (isLivekitJoinedHere && self.channelId === channelId && self.status !== 'idle') {
      clearSelf()
    }
  }, [isLivekitJoinedHere, self.channelId, self.status, channelId, clearSelf])

  if (self.channelId !== channelId) return null
  if (self.status !== 'waiting' && self.status !== 'rejected') return null
  if (isLivekitJoinedHere) return null

  const cancel = () => {
    if (self.waitingId) {
      try { getSocket().emit('voice:waiting:cancel', { channelId, waitingId: self.waitingId }) } catch {}
    }
    clearSelf()
    leaveChannel()
  }

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/90 backdrop-blur-sm">
      <div className="max-w-sm w-full mx-4 rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center mb-3">
            {self.status === 'rejected' ? <X size={28} /> : <Lock size={28} />}
          </div>
          {self.status === 'waiting' && (
            <>
              <div className="text-base font-semibold text-foreground">Waiting for host to admit you</div>
              <div className="mt-1 text-sm text-muted-foreground">
                The room is locked. We'll let you in as soon as the host approves.
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 size={12} className="animate-spin" />
                <span>Waiting · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</span>
              </div>
            </>
          )}
          {self.status === 'rejected' && (
            <>
              <div className="text-base font-semibold text-foreground">We're tied up at the moment</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Sorry — please try again in a few minutes.
              </div>
            </>
          )}
        </div>
        <div className="px-6 pb-5">
          <button
            onClick={cancel}
            className="w-full py-2 rounded-lg bg-muted text-foreground hover:bg-muted/80 transition text-sm font-medium"
          >
            {self.status === 'rejected' ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}
